import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { useTranslation } from "react-i18next"
import QRCode from "react-qr-code"

import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import ProductDetails, {
  ProductSpecifications,
} from "@/globalComponents/molecules/productCards/ProductDetails"
import { formatDateForDisplay, getLocale, prettyPassID } from "@/utilities/etc"
import { parkPassesQueryOptions } from "@/utilities/hooks/queries/park"
import { getPassSessionLengthCopy } from "@/utilities/passes"

import styles from "./$passID.module.css"

export const Route = createFileRoute("/_authenticated/account/passes/$passID")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const rsp = await context.queryClient.ensureQueryData(
      parkPassesQueryOptions()
    )

    const pass = rsp.passes.find((p) => p.passID === params.passID)

    if (!pass) {
      throw notFound()
    }

    return {
      crumb: { label: pass.passName },
    }
  },
})

function RouteComponent() {
  const params = Route.useParams()
  const { data } = useSuspenseQuery(parkPassesQueryOptions())
  const pass = data.passes.find((p) => p.passID === params.passID)!
  const { t, i18n } = useTranslation(["passes", "park"])
  const search = Route.useSearch()

  const passDetails = getPassDetails({ pass, t, i18n })

  return (
    <>
      <Card className={styles.card}>
        <Heading level={3}>{t("park:myPasses.detail.validAt")}</Heading>
        <Text>{pass.locationName}</Text>
        <Text style={{ marginTop: "0" }}>{pass.locationAddress.address}</Text>

        <Heading level={3}>{t("park:myPasses.detail.passDetails")}</Heading>
        <div className={styles.passDetails}>
          <ProductDetails details={passDetails} />
        </div>
      </Card>

      {search.qrLookupID && (
        <div className={styles.qrSection}>
          <Text className="body--4 bold center">
            {t("park:myPasses.detail.qrCodeHelp")}
          </Text>
          <QRCode
            value={prettyPassID(pass.passID)}
            size={86}
            bgColor="var(--neutral-1)"
            fgColor="var(--primary-12)"
            level="M"
          />
          <Text className="body--4 center">{prettyPassID(pass.passID)}</Text>
        </div>
      )}
    </>
  )
}

function getPassDetails(args: {
  pass: Pass
  t: ReturnType<typeof useTranslation<["passes", "park"]>>["t"]
  i18n: ReturnType<typeof useTranslation>["i18n"]
}): ProductSpecifications {
  const { pass, t, i18n } = args

  if (pass.passSpecifications) {
    const details: ProductSpecifications = [
      {
        icon: "pass",
        copy: t("purchase.index.card.specifications.quotaPack.quota", {
          count: pass.maxUses - pass.uses,
          date: formatDateForDisplay({
            isoDateString: pass.passSpecifications.validTo,
            locale: i18n.resolvedLanguage || getLocale(),
            monthFormat: "short",
          }),
        }),
      },
    ]

    const sessionLengthCopy = getPassSessionLengthCopy({
      redemptionConfig: pass.passSpecifications.redemptionConfig,
      locale: i18n.resolvedLanguage,
      t,
      mode: "details",
    })
    if (sessionLengthCopy) {
      details.push({ icon: "clock", copy: sessionLengthCopy })
    }

    if (pass.passSpecifications.subjectToEventRestrictions) {
      details.push({
        icon: "disco",
        copy: t(
          "purchase.index.card.specifications.common.subjectToEventRestrictions"
        ),
      })
    }

    return details
  }

  // Fallback to passNotes - replace {uses} template variable with remaining uses
  const remainingUses = pass.maxUses - pass.uses
  return pass.passNotes.map((note) =>
    note.replace("{uses}", String(remainingUses))
  )
}
